/**
 * A tick box, drawn rather than left to the browser.
 *
 * `accent-color` was what the board used, and it only recolours the platform
 * control: the box keeps the operating system's size, its corner radius and its
 * focus ring, so a row of them sits at a different scale from every other
 * control on the page and looks like something that was never designed.
 *
 * So the native box is switched off with `appearance-none` and the tick is an
 * SVG shown by `peer-checked`. The input is still a real checkbox — it keeps
 * its keyboard behaviour, its label association and its place in the tab order,
 * and the SVG is `aria-hidden` and cannot be clicked, so nothing here is
 * standing in for a control.
 *
 * **Indeterminate is a property, not an attribute**, so it is set through a ref
 * rather than in the markup — React will not write it, and a select-all box
 * over a half-ticked list has no other honest state.
 */
import { useEffect, useRef } from 'react'

export function Checkbox({
  checked,
  indeterminate = false,
  disabled = false,
  id,
  label,
  onChange,
}: {
  readonly checked: boolean
  /** Some but not all of what this box stands for. */
  readonly indeterminate?: boolean
  readonly disabled?: boolean
  /**
   * Given only where there is visible text beside the box.
   *
   * That text is a `<label htmlFor>`, so clicking the words toggles the box —
   * and the pair is what a linter can see, which an `aria-label` alone is not.
   */
  readonly id?: string
  /** Always given: most of these sit in table cells with nothing to read. */
  readonly label: string
  readonly onChange: () => void
}) {
  const box = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (box.current !== null) box.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])

  return (
    <span className="relative inline-flex size-[18px] shrink-0 align-middle">
      <input
        aria-label={label}
        checked={checked}
        className="peer size-[18px] cursor-pointer appearance-none rounded-[5px] border border-line bg-surface transition-colors checked:border-brand checked:bg-brand indeterminate:border-brand indeterminate:bg-brand hover:border-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        id={id}
        onChange={onChange}
        ref={box}
        type="checkbox"
      />
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 m-auto hidden size-[12px] text-brand-contrast peer-checked:block"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
        viewBox="0 0 12 12"
      >
        <path d="M2 6.3 4.7 9 10 3.2" />
      </svg>
      {/* The dash for "some of these". A tick would claim all of them. */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 m-auto hidden size-[12px] text-brand-contrast peer-indeterminate:block"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.4"
        viewBox="0 0 12 12"
      >
        <path d="M2.6 6h6.8" />
      </svg>
    </span>
  )
}
